import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { type ColumnDef } from '@tanstack/react-table';
import { ChevronDown, FileText, Loader2, Plus, Search } from 'lucide-react';
import { useClients, useInvoices } from '@repo/queries';
import type { Client, Invoice } from '@repo/api-client/dtos';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { EmptyState } from '@/components/ui/empty-state';
import { Skeleton } from '@/components/ui/skeleton';
import { Page, PageHeader } from '@/features/common/PageHeader';
import { DataTable } from '@/features/table/DataTable';
import { displayWireMoney } from '@/features/money';
import { describeAppError } from '@/features/common/describeAppError';
import { Alert } from '@/components/ui/alert';
import { ClientAvatar, inferClientTag } from '@/features/avatar';
import { useActiveRole } from '@/features/auth/useActiveRole';

const STATUSES = ['all', 'draft', 'pending', 'sent', 'paid', 'overdue', 'cancelled'] as const;
type StatusFilter = (typeof STATUSES)[number];

export function InvoicesListScreen() {
  const navigate = useNavigate();
  const { canEditOrg } = useActiveRole();
  const {
    invoices,
    loading,
    error,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useInvoices();
  // Look up the matching client row so we can paint each invoice's
  // avatar with the user-supplied photo (when present) instead of
  // falling back to bare initials. The list query is already cached
  // by the clients screen, so this is essentially free.
  const { clients } = useClients();
  const clientById = useMemo(() => {
    const m = new Map<string, Client>();
    for (const c of clients ?? []) m.set(c.id, c);
    return m;
  }, [clients]);

  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<StatusFilter>('all');

  const filtered = useMemo(() => {
    if (!invoices) return [];
    const q = search.trim().toLowerCase();
    return invoices.filter((inv) => {
      if (status !== 'all' && inv.status !== status) return false;
      if (!q) return true;
      return (
        inv.invoiceNumber.toLowerCase().includes(q) ||
        inv.clientNameSnapshot.toLowerCase().includes(q)
      );
    });
  }, [invoices, search, status]);

  const columns = useMemo<ColumnDef<Invoice>[]>(
    () => [
      {
        accessorKey: 'invoiceNumber',
        header: 'Number',
        cell: ({ row }) => (
          <span className="font-mono text-sm font-medium text-foreground">
            {row.original.invoiceNumber}
          </span>
        ),
      },
      {
        accessorKey: 'clientNameSnapshot',
        header: 'Client',
        cell: ({ row }) => {
          const inv = row.original;
          const c = clientById.get(inv.clientId);
          const name = inv.clientNameSnapshot;
          return (
            <span className="flex items-center gap-2.5">
              <ClientAvatar
                name={name || '?'}
                photoUrl={c?.photoUrl ?? null}
                tag={inferClientTag({ name: name ?? '', company: c?.company })}
                size={28}
                fontSize={11}
              />
              <span className="truncate text-sm text-foreground">{name}</span>
            </span>
          );
        },
      },
      {
        accessorKey: 'issueDate',
        header: 'Issued',
        cell: ({ row }) => formatDate(row.original.issueDate),
      },
      {
        accessorKey: 'dueDate',
        header: 'Due',
        cell: ({ row }) => formatDate(row.original.dueDate),
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }) => <StatusBadge status={row.original.status} />,
      },
      {
        accessorKey: 'amountDue',
        header: () => <span className="text-right">Amount due</span>,
        cell: ({ row }) => (
          <span className="block text-right font-mono text-sm font-medium text-foreground">
            {displayWireMoney(row.original.totals.amountDue, row.original.currency)}
          </span>
        ),
      },
    ],
    [clientById],
  );

  return (
    <Page>
      <PageHeader
        title="Invoices"
        description="Send, track, and reconcile every invoice across your active business."
        actions={
          canEditOrg ? (
            <Button asChild>
              <Link to="/invoices/new">
                <Plus className="size-4" /> New invoice
              </Link>
            </Button>
          ) : null
        }
      />

      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="relative max-w-sm flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by number or client"
            className="pl-9"
          />
        </div>
        <div className="flex flex-wrap gap-1.5">
          {STATUSES.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setStatus(s)}
              className={`rounded-full border px-3 py-1 text-xs capitalize transition-colors ${
                status === s
                  ? 'border-primary bg-primary text-primary-foreground'
                  : 'border-line bg-card text-muted-foreground hover:border-line-strong'
              }`}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      {error ? (
        <Alert variant="danger" className="mb-4">
          {describeAppError(error).message}
        </Alert>
      ) : null}

      {loading ? (
        <div className="space-y-2">
          {Array.from({ length: 6 }).map((_, idx) => (
            <Skeleton key={idx} className="h-14 rounded-lg" />
          ))}
        </div>
      ) : (
        <DataTable
          columns={columns}
          data={filtered}
          onRowClick={(row) => navigate(`/invoices/${row.id}`)}
          emptyState={
            <EmptyState
              icon={<FileText className="size-7" />}
              title={search || status !== 'all' ? 'No invoices match' : 'No invoices yet'}
              description={
                search || status !== 'all'
                  ? 'Try a different filter or search term.'
                  : 'Draft your first invoice to start getting paid.'
              }
              actions={
                search || status !== 'all' || !canEditOrg ? null : (
                  <Button asChild>
                    <Link to="/invoices/new">
                      <Plus className="size-4" /> New invoice
                    </Link>
                  </Button>
                )
              }
            />
          }
        />
      )}

      {hasNextPage ? (
        <div className="mt-4 flex justify-center">
          <Button
            variant="outline"
            onClick={fetchNextPage}
            disabled={isFetchingNextPage}
          >
            {isFetchingNextPage ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <ChevronDown className="size-4" />
            )}
            Load more
          </Button>
        </div>
      ) : null}
    </Page>
  );
}

function StatusBadge({ status }: { status: Invoice['status'] }) {
  const variant: 'success' | 'warning' | 'danger' | 'default' | 'accent' =
    status === 'paid'
      ? 'success'
      : status === 'overdue'
        ? 'danger'
        : status === 'sent' || status === 'pending'
          ? 'warning'
          : 'default';
  return (
    <Badge variant={variant} className="capitalize">
      {status}
    </Badge>
  );
}

function formatDate(value: string | null | undefined) {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleDateString();
  } catch {
    return value;
  }
}
